import moment from "moment";
import {
  FinancialHourlyRow,
  ProductMixRow,
  LocationData,
  HourlyBucket,
  ProductRanking,
  EventStats,
  ProcessedReport,
} from "./types";

export const usd = (cents: number) => {
  const sign = cents < 0 ? "-" : "";
  return `${sign}$${(Math.abs(cents) / 100)
    .toFixed(2)
    .replace(/\d(?=(\d{3})+\.)/g, "$&,")}`;
};

const rowGrossCents = (row: FinancialHourlyRow) =>
  (row.gross_credit_sales || 0) +
  (row.gross_rfid_sales || 0) +
  (row.gross_cash_balance_sales || 0) +
  (row.gross_cash_sales || 0) +
  (row.gross_token_sales || 0) +
  (row.gross_promo_balance_sales || 0);

const buildLocations = (rows: FinancialHourlyRow[]): LocationData[] => {
  const groups: Record<string, FinancialHourlyRow[]> = {};
  rows.forEach((row) => {
    const key = `${row.location_id}`;
    if (!groups[key]) groups[key] = [];
    groups[key].push(row);
  });

  return Object.values(groups)
    .map<LocationData>((groupRows) => {
      const first = groupRows[0];

      const hourMap: Record<string, HourlyBucket> = {};
      let gross = 0;
      let tips = 0;
      let tax = 0;
      let refunds = 0;
      let discounts = 0;
      let promoDiscounts = 0;
      let txns = 0;

      groupRows.forEach((row) => {
        const g = rowGrossCents(row);
        gross += g;
        tips += row.total_tips || 0;
        tax += row.total_tax || 0;
        refunds += row.total_refunds || 0;
        discounts += row.total_discounts || 0;
        promoDiscounts += row.total_promo_discounts || 0;
        txns += row.total_transaction_count || 0;

        const hourKey = row.transaction_hour;
        if (!hourMap[hourKey]) {
          hourMap[hourKey] = {
            hour: hourKey,
            label: moment(hourKey).format("ha").toUpperCase(),
            gross: 0,
            txns: 0,
            tips: 0,
          };
        }
        hourMap[hourKey].gross += g;
        hourMap[hourKey].txns += row.total_transaction_count || 0;
        hourMap[hourKey].tips += row.total_tips || 0;
      });

      const hourly = Object.values(hourMap).sort((a, b) =>
        a.hour.localeCompare(b.hour)
      );

      return {
        locationId: first.location_id,
        locationName: first.location_name,
        vendorName: first.vendor_name,
        eventName: first.event_name,
        gross,
        netSales: gross - tax - refunds - discounts - promoDiscounts,
        tips,
        tax,
        txns,
        hourly,
      };
    })
    .sort((a, b) => b.gross - a.gross);
};

const buildProducts = (rows: ProductMixRow[]): ProductRanking[] => {
  const map: Record<string, ProductRanking> = {};
  rows.forEach((row) => {
    const name = row.pos_name || row.description || "Unknown Item";
    const key = `${row.vendor_id}::${name}`;
    if (!map[key]) {
      map[key] = {
        posName: name,
        vendorName: row.vendor_name,
        unitsSold: 0,
        gross: 0,
        avgPrice: 0,
      };
    }
    map[key].unitsSold += row.total_units_sold || 0;
    map[key].gross += row.total_gross_sales || 0;
  });

  Object.values(map).forEach((p) => {
    p.avgPrice = p.unitsSold > 0 ? Math.round(p.gross / p.unitsSold) : 0;
  });

  return Object.values(map).filter((p) => p.unitsSold > 0);
};

const buildEventStats = (locations: LocationData[]): EventStats => {
  const stats = locations.reduce(
    (acc, l) => ({
      totalGross: acc.totalGross + l.gross,
      netSales: acc.netSales + l.netSales,
      tips: acc.tips + l.tips,
      tax: acc.tax + l.tax,
      totalTransactions: acc.totalTransactions + l.txns,
      locationCount: acc.locationCount + 1,
    }),
    { totalGross: 0, netSales: 0, tips: 0, tax: 0, totalTransactions: 0, locationCount: 0 }
  );
  return {
    ...stats,
    avgTicket:
      stats.totalTransactions > 0
        ? Math.round(stats.totalGross / stats.totalTransactions)
        : 0,
  };
};

export const processData = (
  financialRows: FinancialHourlyRow[],
  productRows: ProductMixRow[]
): ProcessedReport => {
  const locations = buildLocations(financialRows);
  const products = buildProducts(productRows);
  const eventStats = buildEventStats(locations);
  const eventName = financialRows[0]?.event_name ?? "";
  return { eventStats, locations, products, eventName };
};
